/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Exporting from 'highcharts/modules/exporting';
import Accessibility from 'highcharts/modules/accessibility';
import './ChargingVsIdleChart.css';
import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { SelectDropDown, TableSubHeader, useAppDispatch, useAppSelector, useEffectOnce } from '../../../globalUtils/globalExports';
import { formatNumberWithCommas, getValueInSeconds, renderHeaderActionButtons } from '../../../globalUtils/globalHooks';
import { RectangularSkeleton } from '../../../globalUtils/SkeletonDesign/SkeletonDesign';
import { getAcDashboardReducer, getAcReferenceDataReducer } from '../../rmsReduxStore/acReduxExports';
import { getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports';
import { getAcFilterSelectedValue, getCurrentAcFiltersValues, getCurrentScreenAppliedAcFiltersCount, setAcFilterData } from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import AcCustomFilterLayout, { AcCustomFilterProp } from './AcCustomFilterLayout';

// Load the variable module if you want different chart types
Exporting(Highcharts);
Accessibility(Highcharts);

interface ChargingVsIdleProp {
  handleFilter: Function,
  getFilterValue: Function
}

const AcChargingVsIdleChart: React.FC<ChargingVsIdleProp> = (props) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { handleFilter, getFilterValue } = props;
  const { chargingStationReferenceData,chargerVisibleIdReferenceData } = useAppSelector(getAcReferenceDataReducer);
  const {intervalRangeReferenceData} = useAppSelector(getReferenceDataReducer);
  const { newDashboardChargingVsIdleInfo } = useAppSelector(getAcDashboardReducer);
  // const [filterValue, setFilterValue] = useState<IGeneralReferenceData>()
  const dispatch = useAppDispatch();
  const navigationUpperLimit = 31
  const [visibleMin, setVisibleMin] = useState(0);
  const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
  const dataLength = newDashboardChargingVsIdleInfo?.length || 0;
  const widgetHeight = 400;
  const [filterAnchorEl,setFilterAnchorEl] = useState<HTMLElement | null>(null);

  // useEffectOnce(() => {
  //   setFilterValue(getFilterValue('idleChargingIntervalRange'))
  // })

  const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const rawValue = this.value;
    const label = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
    const [day, month] = label.split(' ');
  
    const chartWidth = this.axis.chart.chartWidth;
    const numCategories = this.axis.categories?.length || 1;
    const approxCategoryWidth = chartWidth / numCategories;
  
    const shouldWrap = approxCategoryWidth > 0; // adjust threshold if needed
  
    return shouldWrap
      ? `<div style="text-align: center;">${day}<br>${month}</div>`
      : `<div style="text-align: center;">${day}<br><span style="opacity: 0.6; font-size: 10px;">${month}</span></div>`;
  }; 

  const graphInfoContent =
  <div className="tooltip_container">
    <ul>
      <li>Charging time and number of alarms are plotted on the left Y-axis.</li>
      <li>Idle time is plotted on the right Y-axis.</li>
    </ul>
  </div>

  const formatDuration = (seconds: number,shortAbb = true) => {
      if (seconds === 0) return shortAbb ? '0 s' : '0 Sec';
    
      const units = [
        { label: 'Hour',shortAbb: 'h',  value: Math.floor((seconds) / 3600) },
        { label: 'Min',shortAbb: 'm',   value: Math.floor((seconds % 3600) / 60) },
        { label: 'Sec', shortAbb: 's',  value: Math.floor(seconds % 60) }
      ];
  
      const nonZeroUnits = units.filter(unit => unit.value > 0).slice(0, 2);
  
      return nonZeroUnits
        .map(unit => `${unit.label == 'Hour' ? formatNumberWithCommas(unit.value || 0):unit.value}${shortAbb? unit?.shortAbb : (' ' + unit.label)}${(unit.value) > 1 && !shortAbb ? 's' : ''}`)
        .join(' ');
    }
  
    const formatTimeData = (val: number) => {
      const valInSeconds = getValueInSeconds(val, 'h');
      const formattedData = formatDuration(valInSeconds);
      return formattedData;
    }

  const getFormattedData = useCallback(() => {
    const data = newDashboardChargingVsIdleInfo || [];
  
    const slicedData = data.slice(visibleMin, visibleMax + 1);
  
    const xAxis = slicedData.map(item => item?.date);
    const idleSeries = slicedData.map(item => (item?.idleTime ?? 0)/60);
    const chargingSeries = slicedData.map(item => (item?.chargingTime ?? 0)/60);
    const alarmCount = slicedData.map(item => item?.alarmCount ?? 0)
  
    return {
      xAxis,
      series: {
        idle: idleSeries,
        charging: chargingSeries,
        alarmCount: alarmCount
      }
    };
  }, [newDashboardChargingVsIdleInfo, visibleMin, visibleMax]);

  let labelStep = 1;  //draft
  const { xAxis } = getFormattedData();
  const totalBars = xAxis.length;
  if (totalBars > 20) labelStep = 4;
  else if (totalBars > 10) labelStep = 2;

  const handleNavigation = (direction: 'left' | 'right') => {
    const step = navigationUpperLimit + 1;
    if (direction === 'left') {
        setVisibleMin(Math.max(visibleMin - step, 0));
        setVisibleMax(Math.max(visibleMax - step, step));
    } else {
        setVisibleMin(Math.min(visibleMin + step, dataLength - (step + 1)));
        setVisibleMax(Math.min(visibleMax + step, dataLength - 1));
    }
  };
  
  
  getFormattedData();
  const { series } = getFormattedData();
  const options: Highcharts.Options = {
    chart: {
        type: 'column', // specify a default type
        backgroundColor: 'transparent',
        height: widgetHeight,
        spacingBottom: 5,
        events: {
          render: function () {
            const chart = this as Highcharts.Chart & any;
    
            // Remove existing custom arrows before adding new ones
            const existingArrows = chart.container.getElementsByClassName('custom-arrow');
            while (existingArrows.length > 0) {
              existingArrows[0].remove();
            }
    
            // Left arrow (shifted more inside by changing left position)
            if (visibleMin > 0) {
              chart.renderer.image(
                '/images/left_arrow_highchart.svg',
                chart.plotLeft - 10, // shifted inside
                chart.plotTop + chart.plotHeight / 2 - 10,
                20,
                20
              )
              .addClass('custom-arrow left')
              .on('click', () => handleNavigation('left'))
              .add();
            }
    
            // Right arrow (shifted more inside by changing left position)
            if (visibleMax < dataLength - 1) {
              chart.renderer.image(
                '/images/right_arrow_highchart.svg',
                chart.plotLeft + chart.plotWidth - 10, // shifted inside
                chart.plotTop + chart.plotHeight / 2 - 10,
                20,
                20
              )
              .addClass('custom-arrow right')
              .on('click', () => handleNavigation('right'))
              .add();
            }
          }
        },
      } as Highcharts.ChartOptions & { zoomType?: 'x' | 'y' | 'xy' },
      title: { text: undefined },
      subtitle: { text: undefined },
      credits:{
        enabled:false
    },
    exporting: { enabled: false },
    xAxis: [{
      categories: xAxis,
      crosshair: false,
      lineColor: '#3347E6',
      gridLineWidth: (newDashboardChargingVsIdleInfo?.length ?? 0) > 10 ? 0 : 1,
      gridLineDashStyle: 'ShortDash', 
      plotBands: getFormattedData().xAxis.map((_, index) => {
        const barWidthFraction = 0.25;
        return {
          from: index - barWidthFraction,
          to: index + barWidthFraction,
          color: '#f5f5f5'
        };
      }),  
      labels: {
        rotation: 0, 
        step: labelStep,
        formatter: xAxisLabelFormatter,
        style: {
          whiteSpace: 'nowrap', 
        }
      }
    }],
    yAxis: [{ // Primary yAxis
      // tickInterval: 6,
      gridLineWidth: 1,
      gridLineDashStyle: 'ShortDash',
      // labels: {
      //   format: '{value}',
        // style: {
        //   color: '#000'
        // },
      // },
      title: {
        text: 'Charging Time (Hours)',
        // style: {
        //   color: '#000'
        // }
      }
    },
    {
      title: { text: 'Idle Time (Hours)' },
      gridLineWidth: 1,
      gridLineDashStyle: 'ShortDash',
      opposite: true, 
      // gridLineWidth: 0, 
      labels: {
        format: '{value}',
        style: { color: '#000' }
      }
    }
  ],
    tooltip: {
      shared: true,
      formatter: function () {
        const points = this.points || [];
        const category = this.x;

        return `
          ${category}
          ${points.map((point, idx) => {
            const name = point.series.name;
            const value = idx === 2 ? point?.y?.toFixed(0) || 0 : formatTimeData(point.y || 0);
            const color = point.color;
            return `<br/><span style="color:${color}">\u25CF</span> ${name}: <b>${value}</b>`;
          }).join('')}
        `;
      }
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: {
        fontWeight: 'normal'
      },
      useHTML: true,
      labelFormatter: function () {
        const isVisible = this.visible !== false;
        const textStyle = isVisible ? '' : 'color: #999; text-decoration: line-through;';
        const boxColor = isVisible ? this.color : '#ccc';
    
        return `
          <span style="display: inline-flex; align-items: center; gap: 6px; ${textStyle}">
            <span style="width: 10px; height: 10px; background-color: ${boxColor}; display: inline-block; border-radius: 50%;"></span>
            <span>${this.name}</span>
          </span>
        `;
      },
      symbolHeight: 0,
      symbolPadding: 0,
      symbolRadius: 0
    },
    plotOptions: {
      column: {borderRadius: 8}
    },
    series: [{
      name: 'Idle',
      type: 'column',
      yAxis: 1,
      data: series.idle,
      color: '#1f4e79',
    }, {
      name: 'Charging',
      type: 'column',
      data: series.charging,
      color: '#60c18c'
    }, {
      name: 'No. of Alarms',
      type: 'line',
      // yAxis: 1,
      data: series.alarmCount,
      color: '#D21616',
      marker: {
        enabled: false
      }
      // marker: {
      //   lineWidth: 2,
      //   lineColor: '#d13a3a',
      // }
    }]
  };

  // const handleChargingSessionFilter = useCallback(async (val: any, filterKey: string): Promise<void> => {
  //   const res = await setFilterData(val, filterKey, 'dashboardV2', 'SET', false)(dispatch)
  //   if (res?.message === 'Action dispatched successfully') {
  //       handleFilter(res?.filters);
  //   }
  // }, [setFilterData])

  // const handleSelectChange = (val): void => {
  //   const cardValue = val?.label === 'All' ? 'TOTAL' : val?.value?.toString()?.toLocaleUpperCase()
  //   setFilterValue(val);
  //   handleChargingSessionFilter(val, 'idleChargingIntervalRange');
  //   handleChartPageReset();
  // };

  const handleChartPageReset = () => {
    setVisibleMin(0);
    setVisibleMax(navigationUpperLimit);
  }

  const headerActionBtns = renderHeaderActionButtons({
      actionBtns:
          [
              {
                  id: 'add_filter_btn',
                  isAccessible: true,
                  headerActionBtnClick: (e): void => {
                      setFilterAnchorEl(e.currentTarget)
                  },
                  badgeCount: getCurrentScreenAppliedAcFiltersCount('chargingVsIdle',true)
              },
          ],
    })
  
    const handleFilterDataChange = async (val, filterKey, isGlobal) => {
        const res = await setAcFilterData(val, filterKey, 'chargingVsIdle', 'SET', isGlobal ? isGlobal : false,true)(dispatch)
        const dashboardV2Filters = getCurrentAcFiltersValues('acdashboard')
        if (res?.message === 'Action dispatched successfully') {
          handleFilter(dashboardV2Filters,res?.filters)
          handleChartPageReset();
        }
    }
  
    const chargingVsIdleFilterProps: AcCustomFilterProp[] = useMemo(()=>{
      return [
        {
          filterLabel: 'Charging Stations',
          filterType: 'dropdown',
          filterId: 'chargingStationId',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('chargingStationId', 'chargingVsIdle', false),
            selectOptions: chargingStationReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargingStationId', false)
            },
            selectDropDownId: 'charging-vs-idle-chargingStationId-type-filter-dropdown',
            isMultiSelect: true,
            renderToBody:true,
          }
        },
        {
          filterLabel: 'Chargers',
          filterId: 'chargerId',
          filterType: 'dropdown',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('chargerId', 'chargingVsIdle', false),
            selectOptions: chargerVisibleIdReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargerId', false)
            },
            selectDropDownId: 'charging-vs-idle-charger-id-filter-dropdown',
            isMultiSelect: true,
            renderToBody: true
          }
        },
        {
          filterLabel: 'Date Range',
          filterId: 'idleChargingIntervalRange',
          filterType: 'dropdown',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('idleChargingIntervalRange', 'chargingVsIdle', false),
            selectOptions: intervalRangeReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'idleChargingIntervalRange', false)
            },
            selectDropDownId: 'charging-vs-idle-date-range-filter-dropdown',
            isMultiSelect: false,
            renderToBody: true
          }
        },
      ]
    },[intervalRangeReferenceData,chargerVisibleIdReferenceData,chargingStationReferenceData,handleFilterDataChange,getAcFilterSelectedValue])

  return (
    <div className="widget-wrap charging-vs-idle-wrap">
      <div className="widget-header">
        <div className='widget-heading charging-vs-idle-header'>
          Charging vs Idle Time
          <IconWithTooltip iconClassName='info-icon' toolTipText={graphInfoContent} containerClassName={'new-dashboard-widget-header-tooltip'} />
        </div>
        <div className="chart-filters">
          {/* <SelectDropDown
            selectValue={filterValue}
            selectOptions={intervalRangeReferenceData ?? []}
            handleSelectOptionChange={handleSelectChange}
            selectDropDownId='alarm-failed-transaction-dropdown'
            dropDownClassName='alarm-failed-transaction-dropdown'
            isMultiSelect={false}
            placeholder={'Interval'}
            /> */}
          <TableSubHeader headerActionBtns={headerActionBtns}/>
          <AcCustomFilterLayout filters={chargingVsIdleFilterProps} requiredReferenceDataCallbacks={['chargerVisibleId','chargingStationId']} filterAnchorEl={filterAnchorEl} setFilterAnchorEl={setFilterAnchorEl} excludeGlobalFilters={true} />
        </div>
      </div>
      <div className='widget-body'>
      {!newDashboardChargingVsIdleInfo ? (
        <div style={{ height: `${widgetHeight}px` }}>
          <RectangularSkeleton skeletonContainerClassName='' />
        </div>
      ) : newDashboardChargingVsIdleInfo.length === 0 ? (
        <div className='no-data-text'>
          No data available
        </div>
      ) : (
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
        />
      )}
    </div>

    </div>
  );
};

export default AcChargingVsIdleChart;